import { create } from "zustand";

import { workspace } from "@/components/workspace/controller";
import { useWorkspaceStore, type WorkspaceState } from "@/components/workspace/store";
import type { PanelType, WorkspacePanelState } from "@/components/workspace/types";
import { useShell } from "@/stores/shell";

/**
 * Thin delegating shim over the Dockview workspace controller
 * (`components/workspace/controller.ts`). The docview IS the app now — there
 * is no independent tab list to own — so this store no longer holds real
 * state: `tabs`/`activeId` are a reactive PROJECTION of `useWorkspaceStore`'s
 * panels (kept in sync via a `subscribeWithSelector` subscription below, so
 * they stay correct even when a panel opens/closes/renames through a path
 * that never goes through this shim — e.g. `ExplorerSidebar` calling
 * `workspace.openDoc` directly, or Dockview's own tab-close button). Every
 * mutator below just calls into `workspace.*`. Kept only so the ~28 existing
 * `useTabs`/`useTabs.getState()` call sites across the app keep working
 * unchanged; new code should prefer the `workspace` controller directly.
 */
export type TabKind = "graph" | "reader" | "ask" | "orbits" | "markdown";

export interface TabDesc {
  id: string;
  kind: TabKind;
  title: string;
  /** reader/markdown: the document/node id; ask: optional scope */
  docId?: string;
  scopeDocIds?: string[];
  scopeNodeIds?: string[];
  seedQuery?: string;
  /** ask: the backend conversation id, assigned on the first answer. */
  conversationId?: string;
}

/** Panel type → legacy tab kind. PARTIAL on purpose: panel types added after
 * this shim froze (Home) have no legacy equivalent, and `toTabDesc` drops
 * them from the projection rather than inventing a kind that `open()` and the
 * ~28 `useTabs` call sites would then have to guess at. New code talks to the
 * `workspace` controller directly, where Home is a first-class panel. */
const KIND_OF: Partial<Record<PanelType, TabKind>> = {
  doc: "reader",
  markdown: "markdown",
  graph: "graph",
  orbit: "orbits",
  ask: "ask",
};

/** Legacy bare ids ("graph"/"orbits") every call site still uses → the real,
 * singleton workspace panel ids. */
const ALIAS: Record<string, string> = { graph: "graph:main", orbits: "orbit:main" };
const resolveId = (id: string): string => ALIAS[id] ?? id;
const panelExists = (id: string): boolean => !!useWorkspaceStore.getState().panels[resolveId(id)];

function toTabDesc(p: WorkspacePanelState): TabDesc | null {
  const kind = KIND_OF[p.type];
  if (!kind) return null;
  const data = p.data ?? {};
  return {
    id: p.id,
    kind,
    title: p.title,
    docId: (data.docId as string | undefined) ?? undefined,
    scopeDocIds: data.scopeDocIds as string[] | undefined,
    scopeNodeIds: data.scopeNodeIds as string[] | undefined,
    seedQuery: data.seedQuery as string | undefined,
    conversationId: data.conversationId as string | undefined,
  };
}

interface DerivedTabs {
  tabs: TabDesc[];
  activeId: string;
}

function deriveTabs(s: WorkspaceState): DerivedTabs {
  const tabs: TabDesc[] = [];
  for (const p of Object.values(s.panels)) {
    const t = toTabDesc(p);
    if (t) tabs.push(t);
  }
  return { tabs, activeId: s.activeId ?? "" };
}

function sameArr(a: string[] | undefined, b: string[] | undefined): boolean {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
}

function tabDescEqual(a: TabDesc, b: TabDesc): boolean {
  return (
    a.id === b.id &&
    a.kind === b.kind &&
    a.title === b.title &&
    a.docId === b.docId &&
    a.conversationId === b.conversationId &&
    a.seedQuery === b.seedQuery &&
    sameArr(a.scopeDocIds, b.scopeDocIds) &&
    sameArr(a.scopeNodeIds, b.scopeNodeIds)
  );
}

/** Skip the mirror update (and every downstream re-render) when nothing
 * TabDesc-relevant changed — e.g. a floating panel's x/y/zIndex during a drag
 * patches `panels` every pointer-move frame, but never changes id/kind/
 * title/data, so this keeps `useTabs` consumers quiet through a drag. */
function derivedEqual(a: DerivedTabs, b: DerivedTabs): boolean {
  return a.activeId === b.activeId && a.tabs.length === b.tabs.length && a.tabs.every((t, i) => tabDescEqual(t, b.tabs[i]!));
}

interface TabsState extends DerivedTabs {
  open: (tab: Omit<TabDesc, "id"> & { id?: string }) => string;
  openReader: (docId: string, title: string) => void;
  /** Open a note's markdown body in the rich editor (its own panel). */
  openMarkdown: (nodeId: string, title: string) => void;
  openAsk: (opts?: Partial<Pick<TabDesc, "scopeDocIds" | "scopeNodeIds" | "seedQuery" | "title">>) => void;
  /** Seed queries fire exactly once — never again on reload (they persist). */
  consumeSeed: (id: string) => void;
  /** Record the backend conversation id once an Ask panel gets its first answer. */
  setTabConversation: (id: string, conversationId: string) => void;
  /**
   * The conversation a pin should attach to: the active Ask panel's
   * conversation, else the most-recently-opened Ask panel's conversation,
   * else undefined (no Ask panel open → the pin lands in "Unsorted").
   */
  activeConversationId: () => string | undefined;
  close: (id: string) => void;
  /** Close panels tied to a deleted node/document (its reader/markdown + scoped asks). */
  closeForNode: (nodeId: string, docId?: string | null) => void;
  activate: (id: string) => void;
}

export const useTabs = create<TabsState>()((_set, get) => ({
  tabs: [],
  activeId: "",

  open: (tab) => {
    // A reader/markdown tab with no docId has nothing to open — bail BEFORE the
    // openAsk fall-through below, which would otherwise silently open a wrong,
    // unrelated Ask panel (a hard-to-diagnose trap). No-op with a dev warning.
    if ((tab.kind === "reader" || tab.kind === "markdown") && !tab.docId) {
      if (import.meta.env.DEV) {
        console.warn(`useTabs.open: ignoring a "${tab.kind}" tab with no docId`, tab);
      }
      return "";
    }
    if (tab.kind !== "graph") useShell.getState().closeAll();
    if (tab.kind === "graph") return workspace.openGraph();
    if (tab.kind === "orbits") return workspace.openOrbit();
    if (tab.kind === "reader" && tab.docId) {
      return workspace.openDoc({ id: tab.docId, title: tab.title, docKind: "document" });
    }
    if (tab.kind === "markdown" && tab.docId) return workspace.openMarkdown(tab.docId, tab.title);
    return workspace.openAsk({
      scopeDocIds: tab.scopeDocIds,
      scopeNodeIds: tab.scopeNodeIds,
      seedQuery: tab.seedQuery,
      title: tab.title,
    });
  },

  openReader: (docId, title) => {
    useShell.getState().closeAll();
    workspace.openDoc({ id: docId, title, docKind: "document" });
  },

  openMarkdown: (nodeId, title) => {
    useShell.getState().closeAll();
    workspace.openMarkdown(nodeId, title);
  },

  openAsk: (opts) => {
    useShell.getState().closeAll();
    workspace.openAsk({
      scopeDocIds: opts?.scopeDocIds,
      scopeNodeIds: opts?.scopeNodeIds,
      seedQuery: opts?.seedQuery,
      title: opts?.title ?? "ask",
    });
  },

  consumeSeed: (id) => {
    const rid = resolveId(id);
    if (panelExists(rid)) workspace.setData(rid, "seedQuery", undefined);
  },

  setTabConversation: (id, conversationId) => {
    const rid = resolveId(id);
    if (panelExists(rid)) workspace.setData(rid, "conversationId", conversationId);
  },

  activeConversationId: () => {
    const { tabs, activeId } = get();
    const active = tabs.find((t) => t.id === activeId);
    if (active?.kind === "ask" && active.conversationId) return active.conversationId;
    // Fall back to the most-recently-opened Ask panel that has a conversation.
    const asks = tabs.filter((t) => t.kind === "ask" && t.conversationId);
    return asks.at(-1)?.conversationId;
  },

  close: (id) => {
    const rid = resolveId(id);
    if (panelExists(rid)) workspace.closePanel(rid);
  },

  closeForNode: (nodeId, docId) => {
    const toClose = get().tabs.filter(
      (t) =>
        (t.kind === "reader" && !!docId && t.docId === docId) ||
        (t.kind === "markdown" && t.docId === nodeId) ||
        (!!docId && t.scopeDocIds?.includes(docId)) ||
        t.scopeNodeIds?.includes(nodeId),
    );
    for (const t of toClose) get().close(t.id);
  },

  activate: (id) => {
    if (id === "graph") {
      workspace.openGraph();
      return;
    }
    if (id === "orbits") {
      workspace.openOrbit();
      return;
    }
    if (panelExists(id)) workspace.focusPanel(id);
  },
}));

// Keep the mirror in sync with the canonical workspace store, from every
// mutation path (this shim's own methods, the explorer opening a doc
// directly, a Dockview tab close, a drag/detach, hydrate() on load…).
useWorkspaceStore.subscribe(deriveTabs, (next) => useTabs.setState(next), {
  equalityFn: derivedEqual,
  fireImmediately: true,
});
